// ============================================================
// caseDateRules.ts — the free-text case / client DATE rules on the page
// (pure; caseDateRules.test.ts). The server enforces the same BLOCKS (400)
// with the same wording — this shows them at once and stops the autosave
// sending a value it would refuse. WARNINGS (amber) never block.
//
// BLOCK
//   • accident date in the future / before the client's date of birth
//   • date of birth in the future
//   • close date in the future / before the open date
// WARN
//   • date of birth → under 18 today, or over 110
//   • accident date more than 3 years ago (limitation)
//   • task due date in the past, or more than 5 years ahead
// Dates are "YYYY-MM-DD"; stored free-text values in other accepted
// formats ("D/M/YYYY", ISO timestamps) are read with textDateToIso.
// "Today" is always Europe/London (londonToday).
// ============================================================
import { addYearsIso, isRealIsoDate, textDateToIso, ukDate, yearsBetween } from './civilDate'

const iso = (raw: string | null | undefined): string | null => (raw ? textDateToIso(raw) : null)

export const accidentDateError = (accident: string | null | undefined, dobRaw: string | null | undefined, today: string): string | null => {
  if (!isRealIsoDate(accident)) return null
  if (accident > today) return 'Accident date can’t be in the future.'
  const dob = iso(dobRaw)
  if (dob && accident < dob) return `Accident date is before the client’s date of birth (${ukDate(dob)}).`
  return null
}

export const dobError = (dob: string | null | undefined, today: string): string | null =>
  isRealIsoDate(dob) && dob > today ? 'Date of birth can’t be in the future.' : null

export const closeDateError = (close: string | null | undefined, openRaw: string | null | undefined, today: string): string | null => {
  if (!isRealIsoDate(close)) return null
  if (close > today) return 'Close date can’t be in the future.'
  const open = iso(openRaw)
  if (open && close < open) return `Close date is before the open date (${ukDate(open)}).`
  return null
}

// The other side of closeDateError: an open date after the case's close date.
export const openDateError = (open: string | null | undefined, closeRaw: string | null | undefined): string | null => {
  if (!isRealIsoDate(open)) return null
  const close = iso(closeRaw)
  if (close && open > close) return `Open date is after the close date (${ukDate(close)}).`
  return null
}

export const dobWarning = (dob: string | null | undefined, today: string): string | null => {
  const d = iso(dob)
  if (!d || d > today) return null
  const age = yearsBetween(d, today)
  if (age < 18) return 'Under 18 — limitation usually runs from their 18th birthday.'
  if (age > 110) return 'Over 110 — check the date of birth.'
  return null
}

export const accidentDateWarning = (accident: string | null | undefined, today: string): string | null => {
  const a = iso(accident)
  if (!a || a > today) return null
  const threeYearsAgo = addYearsIso(today, -3)
  return threeYearsAgo && a < threeYearsAgo ? 'Accident more than 3 years ago — check limitation.' : null
}

export const taskDueWarning = (due: string | null | undefined, today: string): string | null => {
  if (!isRealIsoDate(due)) return null
  if (due < today) return 'Due date is in the past.'
  const fiveYears = addYearsIso(today, 5)
  if (fiveYears && due > fiveYears) return 'Due date is more than 5 years ahead — check the year.'
  return null
}

// The server's refusal of a case/client date save, for the field it's about.
// Matched on the response's `field` (what the server says it refused) — never
// on the message's wording, which can name a DIFFERENT field (e.g. a DOB
// change refused with "Accident date is before…"). `field` is the API key:
// accidentDate · clientDateOfBirth / dateOfBirth · openDate · closeDate · handover.
export const serverDateErrorFor = (err: unknown, field: string): string | null => {
  const r = (err as { response?: { status?: number; data?: { error?: unknown; field?: unknown } } } | null)?.response
  if (!r || r.status !== 400 || typeof r.data?.error !== 'string') return null
  return r.data.field === field ? r.data.error : null
}
